/**
 * Feedback Service
 *
 * API service for user feedback and NPS.
 */

import { TonnieClient } from '../client/base';

// ═══════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════

export interface FeedbackInput {
  type: 'bug' | 'feature' | 'improvement' | 'general';
  category: string;
  subject: string;
  message: string;
  pageUrl?: string;
  /** 1–5 star rating from the widget. */
  rating?: number;
  /** Which portal filed it: journey-web | business-web | citizen-mobile. */
  portal?: string;
  /**
   * Image attachments (BL-022) — sent as multipart `files` parts and
   * forwarded into Sentry's feedback envelope server-side. Web passes
   * `File`s; React Native passes `{ uri, name, type }` descriptors.
   */
  files?: Array<File | { uri: string; name: string; type: string }>;
}

export interface FeedbackResponse {
  id: string;
  status: 'received' | 'reviewing' | 'resolved';
  message: string;
  /**
   * Whether the submission actually reached the Sentry feedback inbox — the
   * system of record. `status: 'received'` only means the API accepted the
   * request; a no-op Sentry client (missing/invalid DSN) returns the same
   * thing while the feedback goes nowhere (BL-025).
   */
  delivered?: boolean;
  /** How many attachments the server accepted onto the envelope. */
  attachments?: number;
}

export interface NpsInput {
  score: number; // 0-10
  comment?: string;
}

export interface NpsResponse {
  id: string;
  score: number;
  comment?: string;
  submittedAt: string;
}

// ═══════════════════════════════════════════════════════════════════════
// SERVICE
// ═══════════════════════════════════════════════════════════════════════

export class FeedbackService {
  constructor(private client: TonnieClient) {}

  /**
   * Submit feedback. Multipart when attachments are present (image
   * parts + text fields), plain JSON otherwise.
   */
  async submitFeedback(input: FeedbackInput): Promise<FeedbackResponse> {
    const { files, ...fields } = input;

    if (files && files.length > 0) {
      const formData = new FormData();
      for (const [key, value] of Object.entries(fields)) {
        if (value !== undefined && value !== null) {
          formData.append(key, String(value));
        }
      }
      for (const file of files) {
        // RN file descriptors are appended as-is; the fetch polyfill
        // streams them. Browsers append real File objects.
        formData.append('files', file as Blob);
      }
      return this.client.postForm<FeedbackResponse>('/api/feedback', formData);
    }

    return this.client.post<FeedbackResponse>('/api/feedback', fields);
  }

  /**
   * Submit NPS score
   */
  async submitNps(input: NpsInput): Promise<NpsResponse> {
    return this.client.post<NpsResponse>('/api/feedback/nps', input);
  }
}

export function createFeedbackService(client: TonnieClient): FeedbackService {
  return new FeedbackService(client);
}
